import { appendPageViewToLocalLog } from "./pageViewLocalLog";

const SESSION_KEY = "lynsight_pv_session";
const SEQUENCE_KEY = "lynsight_pv_seq";
const DEVICE_ID_KEY = "lynsight_device_id";

function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `fallback-${Date.now()}`;
  }
}

function getDeviceInfo(): string {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent;
  const isMobile = /iPhone|Android.*Mobile|IEMobile|BlackBerry/i.test(ua);
  const isTablet = !isMobile && /iPad|Android|Tablet/i.test(ua);
  const deviceType = isMobile ? "모바일" : isTablet ? "태블릿" : "데스크톱";

  let os = "";
  if (/iPhone|iPad/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";

  let browser = "";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/SamsungBrowser/.test(ua)) browser = "Samsung";
  else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua)) browser = "Safari";

  return [deviceType, os, browser].filter(Boolean).join(" · ");
}

export type PageViewPayload = {
  event: "pageview";
  /** Correlates pageview ↔ page_leave for the same screen */
  clientEventId: string;
  /** pathname only, e.g. /Activities */
  path: string;
  /** pathname + search + hash */
  fullPath: string;
  /** ISO 8601 UTC, second precision (no milliseconds) */
  timestampUtc: string;
  documentTitle?: string;
  referrer?: string;
  language?: string;
  /** Per-tab session id (approx. one visitor session) */
  sessionId: string;
  /** Monotonic counter for this tab session */
  viewSequence: number;
  device?: string;
  deviceId?: string;
};

export type PageLeavePayload = {
  event: "page_leave";
  clientEventId: string;
  path: string;
  fullPath: string;
  /** When this view became active (UTC, seconds) */
  enteredAtUtc: string;
  /** When the user left or hid the view (UTC, seconds) */
  leftAtUtc: string;
  /** Whole seconds spent on this path (minimum 0) */
  dwellSeconds: number;
  sessionId: string;
  viewSequence: number;
  documentTitle?: string;
};

export type ProtectedViewPayload = {
  event: "protected_view";
  clientEventId: string;
  path: string;
  fullPath: string;
  timestampUtc: string;
  sessionId: string;
  viewSequence: number;
  documentTitle?: string;
  device?: string;
  deviceId?: string;
};

export type ContentUnlockedPayload = {
  event: "content_unlocked";
  clientEventId: string;
  path: string;
  fullPath: string;
  timestampUtc: string;
  sessionId: string;
  viewSequence: number;
  documentTitle?: string;
  device?: string;
  deviceId?: string;
};

export type TrackingPayload = PageViewPayload | PageLeavePayload | ProtectedViewPayload | ContentUnlockedPayload;

function timestampUtcSeconds(date: Date = new Date()): string {
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** If empty, all paths are tracked. Otherwise pathname must match a prefix. */
export function getTrackPathPrefixes(): string[] {
  const raw = process.env.REACT_APP_PAGE_VIEW_TRACK_PREFIXES;
  if (!raw || !raw.trim()) {
    return [];
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function shouldTrackPathname(pathname: string): boolean {
  const prefixes = getTrackPathPrefixes();
  if (prefixes.length === 0) {
    return true;
  }
  const path = pathname || "/";
  return prefixes.some((p) => path === p || path.startsWith(`${p}/`));
}

function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `fallback-${Date.now()}`;
  }
}

function nextViewSequence(): number {
  try {
    const raw = sessionStorage.getItem(SEQUENCE_KEY);
    const next = (raw ? Number.parseInt(raw, 10) : 0) + 1;
    sessionStorage.setItem(SEQUENCE_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}

export function newClientEventId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function buildPageViewPayload(
  pathname: string,
  search: string,
  hash: string,
  clientEventId: string
): PageViewPayload {
  const path = pathname || "/";
  const fullPath = `${pathname}${search}${hash}`;
  return {
    event: "pageview",
    clientEventId,
    path,
    fullPath,
    timestampUtc: timestampUtcSeconds(),
    documentTitle: typeof document !== "undefined" ? document.title : undefined,
    referrer: typeof document !== "undefined" && document.referrer ? document.referrer : undefined,
    language: typeof navigator !== "undefined" ? navigator.language : undefined,
    sessionId: getOrCreateSessionId(),
    viewSequence: nextViewSequence(),
    device: getDeviceInfo(),
    deviceId: getOrCreateDeviceId(),
  };
}

const DEDUPE_PREFIX = "lynsight_pv_dedupe:";
const DEDUPE_MS = 2000;

function shouldSkipDuplicatePageview(fullPath: string): boolean {
  try {
    const key = `${DEDUPE_PREFIX}${fullPath}`;
    const now = Date.now();
    const prev = sessionStorage.getItem(key);
    if (prev) {
      const prevTime = Number.parseInt(prev, 10);
      if (!Number.isNaN(prevTime) && now - prevTime < DEDUPE_MS) {
        return true;
      }
    }
    sessionStorage.setItem(key, String(now));
    return false;
  } catch {
    return false;
  }
}

function postTracking(payload: TrackingPayload): void {
  const url = process.env.REACT_APP_PAGE_VIEW_API_URL;
  if (!url) {
    if (process.env.NODE_ENV === "development" && shouldTrackPathname(payload.path)) {
      // eslint-disable-next-line no-console
      console.debug("[PageViewTracker] REACT_APP_PAGE_VIEW_API_URL not set; skipped", payload);
    }
    return;
  }

  const body = JSON.stringify(payload);

  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    mode: "cors",
    credentials: "omit",
  }).catch(() => {
    /* ignore */
  });
}

/**
 * Sends a page view to REACT_APP_PAGE_VIEW_API_URL (POST JSON).
 * Respects REACT_APP_PAGE_VIEW_TRACK_PREFIXES when set (comma-separated path prefixes).
 * @returns true if a request was queued (not skipped by prefix or dedupe).
 */
export function sendPageView(payload: PageViewPayload): boolean {
  if (shouldSkipDuplicatePageview(payload.fullPath)) {
    return false;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  appendPageViewToLocalLog({
    id: payload.clientEventId,
    pageUrl: `${origin}${payload.fullPath}`,
    timestampUtc: payload.timestampUtc,
    event: "pageview",
    device: payload.device,
    deviceId: payload.deviceId,
  });

  if (shouldTrackPathname(payload.path)) {
    postTracking(payload);
  }
  return true;
}

export function sendPageLeave(payload: PageLeavePayload): void {
  if (!shouldTrackPathname(payload.path)) {
    return;
  }
  postTracking(payload);
}

export function sendProtectedView(path: string, fullPath: string): void {
  if (!shouldTrackPathname(path)) {
    return;
  }
  const id = newClientEventId();
  const timestampUtc = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
  const device = getDeviceInfo();
  const deviceId = getOrCreateDeviceId();
  const payload: ProtectedViewPayload = {
    event: "protected_view",
    clientEventId: id,
    path,
    fullPath,
    timestampUtc,
    sessionId: getOrCreateSessionId(),
    viewSequence: nextViewSequence(),
    documentTitle: typeof document !== "undefined" ? document.title : undefined,
    device,
    deviceId,
  };
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  appendPageViewToLocalLog({ id, pageUrl: `${origin}${fullPath}`, timestampUtc, event: "protected_view", device, deviceId });
  postTracking(payload);
}

export function sendContentUnlocked(path: string, fullPath: string): void {
  if (!shouldTrackPathname(path)) {
    return;
  }
  const id = newClientEventId();
  const timestampUtc = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
  const device = getDeviceInfo();
  const deviceId = getOrCreateDeviceId();
  const payload: ContentUnlockedPayload = {
    event: "content_unlocked",
    clientEventId: id,
    path,
    fullPath,
    timestampUtc,
    sessionId: getOrCreateSessionId(),
    viewSequence: nextViewSequence(),
    documentTitle: typeof document !== "undefined" ? document.title : undefined,
    device,
    deviceId,
  };
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  appendPageViewToLocalLog({ id, pageUrl: `${origin}${fullPath}`, timestampUtc, event: "content_unlocked", device, deviceId });
  postTracking(payload);
}

export function recordPageView(
  pathname: string,
  search: string,
  hash: string,
  clientEventId: string
): boolean {
  return sendPageView(buildPageViewPayload(pathname, search, hash, clientEventId));
}

export function buildPageLeavePayload(
  path: string,
  fullPath: string,
  enteredAtMs: number,
  clientEventId: string,
  viewSequence: number
): PageLeavePayload {
  const left = new Date();
  const dwellMs = Math.max(0, left.getTime() - enteredAtMs);
  const dwellSeconds = Math.floor(dwellMs / 1000);
  return {
    event: "page_leave",
    clientEventId,
    path,
    fullPath,
    enteredAtUtc: timestampUtcSeconds(new Date(enteredAtMs)),
    leftAtUtc: timestampUtcSeconds(left),
    dwellSeconds,
    sessionId: getOrCreateSessionId(),
    viewSequence,
    documentTitle: typeof document !== "undefined" ? document.title : undefined,
  };
}
